import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { FormControlLabel, ThemeProvider, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import { ReactElement } from 'react';
import { Controller, ControllerProps, useFormContext } from 'react-hook-form';
import { getCheckboxTheme } from '../../../pages/ListManyCalendars/getCheckboxTheme';
import { UserRole } from '../../../services/api.types';
import { formatClassTitleColor, neutralColors } from '../../../utils/colors';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export type Option = {
  id: number | string;
  label: string;
  role?: UserRole;
};

export interface CheckboxesTagsProps extends Omit<ControllerProps, 'render'> {
  name: string;
  label?: string;
  placeholder?: string;
  limitTags?: number;
  disabled?: boolean;
  startAdornment?: JSX.Element;
  options: Option[];
  colors?: Record<number, string>;
}

export function CheckboxesTags({
  name,
  label,
  placeholder,
  startAdornment,
  limitTags = 4,
  disabled = false,
  options,
  colors,
  ...props
}: CheckboxesTagsProps) {
  const methods = useFormContext();

  return (
    <ThemeProvider theme={getCheckboxTheme()}>
      <FormControlLabel
        className="w-0 min-w-full m-0"
        labelPlacement="top"
        label={
          <Typography
            className={clsx(
              { 'text-mui-error': methods.formState.errors[name] },
              'ml-6 mb-1 font-bold'
            )}
            alignSelf="start"
          >
            {label}
            {props.rules?.required && !disabled && (
              <span className="ml-1 text-mui-error">*</span>
            )}
          </Typography>
        }
        control={
          <Controller
            {...props}
            rules={!disabled ? props.rules : undefined}
            name={name}
            render={({ field, fieldState: { error } }) => (
              <div className="w-full flex justify between gap-4">
                <Autocomplete
                  {...field}
                  fullWidth
                  disabled={disabled}
                  multiple
                  disableCloseOnSelect
                  size="small"
                  placeholder={placeholder}
                  noOptionsText={'Sem opções'}
                  limitTags={limitTags}
                  options={options}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  ChipProps={{
                    style: {
                      backgroundColor: 'transparent',
                      color: 'inherit',
                    },
                  }}
                  onChange={(event, checkedValue) => {
                    methods.setValue(name, checkedValue);
                  }}
                  renderOption={(props, option, state) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        className="mr-2"
                        checked={state.selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={({ fullWidth, InputProps, ...params }) => (
                    <TextField
                      {...params}
                      fullWidth
                      disabled={disabled}
                      error={!!error}
                      helperText={error?.message}
                      placeholder={placeholder}
                      InputProps={{
                        ...InputProps,
                        startAdornment: (
                          <>
                            <span
                              className={
                                startAdornment && 'pl-3 flex items-center'
                              }
                            >
                              {startAdornment}
                            </span>
                            {(
                              InputProps.startAdornment as Array<ReactElement>
                            )?.map((reactElement, idx) => {
                              const userId = options.find((value) => {
                                return value.label === reactElement.props.label;
                              })?.id;
                              let colorValue = colors?.[Number(userId)] ?? '';
                              // Prevent black/gray/white tints for checkboxes
                              const neutralColorsLowercase = neutralColors.map(
                                (c) => c.toLowerCase()
                              );
                              if (
                                neutralColorsLowercase.includes(
                                  colorValue.toLowerCase() || ''
                                )
                              ) {
                                colorValue = '';
                              }
                              return (
                                <div
                                  style={{
                                    backgroundColor: colorValue,
                                    color:
                                      colorValue &&
                                      formatClassTitleColor(colorValue),
                                  }}
                                  className="m-1 rounded-full truncate"
                                  key={userId ?? idx}
                                >
                                  {reactElement}
                                </div>
                              );
                            })}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>
            )}
          />
        }
      />
    </ThemeProvider>
  );
}
